/* R148d · Feb 2026 — Modal for capturing WHY a task date changed.
 * The reason is sent as `date_change_reason` on the PATCH and the
 * backend logs it into the task's Activity thread with the field
 * name and the new value. */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle } from "lucide-react";

export default function TaskDateChangeReasonDialog({ fieldLabel, previousValue, newValue, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!reason.trim()) { setErr("Please add a short reason so the team has context in the activity log."); return; }
    setErr("");
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } catch (e) {
      setErr(e?.message || "Failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="task-date-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Why is the {fieldLabel} changing?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border bg-muted/20 p-2 text-xs">
            <div><span className="text-muted-foreground">Previous:</span> <span className="font-mono">{previousValue || "—"}</span></div>
            <div><span className="text-muted-foreground">New:</span> <span className="font-mono">{newValue || "—"}</span></div>
          </div>
          <div>
            <div className="label-eyebrow text-[10px] mb-1">Reason for change</div>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Client changed brief mid-week / Dependency slipped / Resource swap"
              className="min-h-[90px]"
              autoFocus
              data-testid="task-date-reason-input"
            />
          </div>
          {err && <div className="text-xs text-red-600" data-testid="task-date-reason-err">{err}</div>}
          <p className="text-[10px] text-muted-foreground">
            This reason will be posted to the task&apos;s Activity thread along with the field name and new value so the whole team sees the context.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onCancel} disabled={busy} data-testid="task-date-reason-cancel">Cancel change</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="task-date-reason-submit">
            {busy ? "Saving…" : "Save & log to activity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
